import type { TokenexBrand } from './components/ui/CardBrandIcon';

export { customCardTypesConfig } from './config/customCardTypesConfig';

export const TokenScheme = {
  Card: 'sixASCIITOKENfour',
  CVV: 'GUID',
} as const;

export const TokenexScriptUrl = {
  test: 'https://test-htp.tokenex.com/Iframe/iframe-v3.82.min.js',
  live: 'https://htp.tokenex.com/Iframe/Iframe-v3.82.min.js',
  'eu-live': 'https://eu1-htp.tokenex.com/Iframe/Iframe-v3.82.min.js',
} as const;

export const BillingFields = [
  'billingFirstName',
  'billingLastName',
  'billingName',
  'billingBirthday',
  'billingDocument',
  'billingDocumentNumber',
  'billingDocumentType',
  'billingTaxId',
  'billingAddress1',
  'billingAddress2',
  'billingState',
  'billingCity',
  'billingZip',
  'billingCountry',
  'billingEmail',
  'billingCompany',
  'billingPhone',
  'billingFax',
] as const;

export const ContactFields = ['firstName', 'lastName', 'fullName'] as const;

export const CardFields = [
  'cardNumber',
  'expiry',
  'cvv',
  'cardholderName',
] as const;

const lsApiUrl = localStorage.getItem('__paym_sdk_api_url') || '';
const lsBaseUrl = localStorage.getItem('__paym_sdk_base_url') || '';

export const ApiHosts = {
  local: {
    host: lsApiUrl || 'https://api.cloudbeds-local.com',
    origin: lsBaseUrl || 'https://hotels.cloudbeds-local.com',
  },
  'local-us2': {
    host: lsApiUrl || 'https://api.cloudbeds-local.com',
    origin: lsBaseUrl || 'https://hotels.cloudbeds-local.com',
  },
  dev: {
    host: lsApiUrl || 'https://api.us2.cloudbeds-stage.com',
    origin: lsBaseUrl || 'https://us2.cloudbeds-stage.com',
  },
  stage: {
    host: lsApiUrl || 'https://api.us1.cloudbeds-stage.com',
    origin: lsBaseUrl || 'https://hotels.cloudbeds-stage.com',
  },
  prod: {
    host: lsApiUrl || 'https://api.us1.cloudbeds.com',
    origin: lsBaseUrl || 'https://hotels.cloudbeds.com',
  },
  'prod-us1': {
    host: lsApiUrl || 'https://api.us1.cloudbeds.com',
    origin: lsBaseUrl || 'https://us1.cloudbeds.com',
  },
  'prod-us2': {
    host: lsApiUrl || 'https://api.us2.cloudbeds.com',
    origin: lsBaseUrl || 'https://us2.cloudbeds.com',
  },
} as const;

export const Events = {
  LoadStart: 'on-load-start',
  LoadEnd: 'on-load-end',
  Change: 'on-change',
  Submit: 'on-submit',
  Validate: 'on-validate',
  Tokenize: 'on-tokenize',
  Error: 'on-error',
  Hello: 'on-hello',
};

/**
 * Maps card brand to human-readable name and standard brand ID.
 */
const cardData = {
  amex: { display: 'American Express', id: 'americanExpress' } as const,
  dinersClub: { display: 'Diners Club', id: 'diners' } as const,
  dankort: { display: 'Dankort', id: 'dankort' } as const,
  discover: { display: 'Discover', id: 'discover' } as const,
  hipercard: { display: 'Hipercard', id: 'hiper' } as const,
  mastercard: { display: 'MasterCard', id: 'masterCard' } as const,
  unionPay: { display: 'UnionPay', id: 'unionPay' } as const,
  eurocard: { display: 'Eurocard', id: 'eurocard' } as const,
  jcb: { display: 'JCB', id: 'jcb' } as const,
  maestro: { display: 'Maestro', id: 'maestro' } as const,
  visa: { display: 'Visa', id: 'visa' } as const,
};

/**
 * Map card brand IDs to human-readable card brand names (for display) and
 * standardized IDs (for processing).
 */
export const cardBrandDataMap: Record<
  string,
  { display: string; id: TokenexBrand }
> = {
  // American Express
  amex: cardData.amex,
  americanexpress: cardData.amex,
  american_express: cardData.amex,
  'american express': cardData.amex,
  // Dankort
  dan: cardData.dankort,
  dankort: cardData.dankort,
  // Diners Club
  diners: cardData.dinersClub,
  dinerscard: cardData.dinersClub,
  diners_card: cardData.dinersClub,
  'diners card': cardData.dinersClub,
  dinersclub: cardData.dinersClub,
  diners_club: cardData.dinersClub,
  'diners club': cardData.dinersClub,
  // Discover
  disc: cardData.discover,
  discover: cardData.discover,
  // Eurocard
  eurocard: cardData.eurocard,
  // Hipercard
  hiper: cardData.hipercard,
  hipercard: cardData.hipercard,
  // JCB
  jcb: cardData.jcb,
  // Maestro
  maestro: cardData.maestro,
  // MasterCard
  master: cardData.mastercard,
  mastercard: cardData.mastercard,
  // China UnionPay
  unionpay: cardData.unionPay,
  union_pay: cardData.unionPay,
  cunion: cardData.unionPay,
  cup: cardData.unionPay,
  china_union_pay: cardData.unionPay,
  china_unionpay: cardData.unionPay,
  'china union pay': cardData.unionPay,
  'china unionpay': cardData.unionPay,
  // Visa
  visa: cardData.visa,
};
